import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LogOut, Shield, User as UserIcon } from 'lucide-react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { api } from '~/lib/api';
import type { ListedUser } from '~/lib/api';
import { useAuth } from '~/lib/auth';

export const Route = createFileRoute('/settings/profile')({
  component: ProfileSettingsPage
});

interface MeResponse {
  user: ListedUser;
}

function ProfileSettingsPage() {
  const { user: sessionUser, logout } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['users', 'me'],
    queryFn: () => api.get<MeResponse>('/api/users/me')
  });

  const user = data?.user ?? sessionUser;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <UserIcon className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Profile</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your account</CardTitle>
          <CardDescription>Sign-in and organization membership</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && !user ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
                  {user?.email?.charAt(0).toUpperCase() ?? 'U'}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium">{user?.email}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Shield className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="text-xs capitalize text-muted-foreground">
                      {user?.role}
                    </span>
                  </div>
                </div>
              </div>
              {data?.user ? (
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">Member since</dt>
                    <dd className="font-medium">
                      {new Date(data.user.createdAt).toLocaleDateString(
                        undefined,
                        {
                          dateStyle: 'long'
                        }
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Last sign-in</dt>
                    <dd className="font-medium">
                      {data.user.lastLoginAt
                        ? new Date(data.user.lastLoginAt).toLocaleString(
                            undefined,
                            {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            }
                          )
                        : '—'}
                    </dd>
                  </div>
                </dl>
              ) : null}
              <p className="text-sm text-muted-foreground">
                You sign in with a magic link sent to this email. To use a
                different address, sign out and request a link for the new email
                (an owner may need to invite you first).
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
          <CardDescription>Manage your active session</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
